import React from 'react';
import { FileText, Pencil, Search, FileSpreadsheet, FileDown, Printer, Receipt, Ban } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/dateUtils';

/**
 * Tabla de facturas con búsqueda, exportación a Excel y acciones por fila.
 */
export const FacturasTable = ({
  facturas,
  facturasFiltradas,
  searchFactura,
  setSearchFactura,
  onExportExcel,
  onVerDetalle,
  onEditFactura,
  onAnularFactura,
  onExportPDF,
}) => (
  <Card>
    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
        Historial de Conduces
      </CardTitle>
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <div className="relative flex-1 sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Buscar por N° o cliente..."
            value={searchFactura}
            onChange={(e) => setSearchFactura(e.target.value)}
            className="pl-9 h-9"
            data-testid="input-buscar-factura"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportExcel}
          disabled={facturasFiltradas.length === 0}
          data-testid="btn-export-facturas-excel"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {facturas.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay conduces registrados</p>
        </div>
      ) : facturasFiltradas.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Sin resultados para "{searchFactura}"</p>
          <Button variant="link" size="sm" onClick={() => setSearchFactura('')} className="mt-2">
            Limpiar búsqueda
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="tabla-facturas">
            <TableHeader>
              <TableRow>
                <TableHead>N° Conduce</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Huevos</TableHead>
                <TableHead className="text-right">Rotos</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {facturasFiltradas.map((fac) => {
                const facId = fac.id || fac._id;
                const estado = fac.estado_pago || 'Pendiente';
                const estadoVariant =
                  estado === 'Pagada' ? 'default'
                    : estado === 'Parcial' ? 'secondary'
                    : estado === 'Anulada' ? 'destructive'
                    : 'outline';
                const isAnulada = estado === 'Anulada';
                return (
                  <TableRow
                    key={facId}
                    className={`hover:bg-accent/60 transition-colors duration-150 ${isAnulada ? 'opacity-60' : ''}`}
                  >
                    <TableCell className={`font-medium ${isAnulada ? 'line-through' : ''}`}>{fac.numero_factura}</TableCell>
                    <TableCell>{formatDate(fac.fecha)}</TableCell>
                    <TableCell>{fac.cliente_nombre}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fac.total_huevos?.toLocaleString('en-US')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fac.total_rotos > 0 ? (
                        <Badge variant="destructive" className="text-xs">
                          {fac.total_rotos?.toLocaleString('en-US')}
                        </Badge>
                      ) : '0'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      ${fac.total_monto?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={estadoVariant} className="text-xs">{estado}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onVerDetalle(fac)}
                        title="Ver detalle"
                        data-testid={`btn-detalle-factura-${facId}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Imprimir / PDF"
                            data-testid={`btn-print-factura-${facId}`}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel className="text-xs">Copia Cliente (sin rotos)</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => onExportPDF(fac, 'print', 'cliente')}>
                            <Printer className="w-4 h-4 mr-2" /> Imprimir
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onExportPDF(fac, 'save', 'cliente')}>
                            <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">Original (con rotos)</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => onExportPDF(fac, 'print', 'interna')}>
                            <Printer className="w-4 h-4 mr-2" /> Imprimir
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onExportPDF(fac, 'save', 'interna')}>
                            <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditFactura(fac)}
                        title="Editar"
                        disabled={isAnulada}
                        data-testid={`btn-edit-factura-${facId}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onAnularFactura(fac)}
                        title={isAnulada ? 'Ya está anulada' : 'Anular conduce'}
                        disabled={isAnulada}
                        data-testid={`btn-anular-factura-${facId}`}
                      >
                        <Ban className={`w-4 h-4 ${isAnulada ? 'text-muted-foreground' : 'text-destructive'}`} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);
